import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CFCacheService } from '../services/cache/cf-cache.service';
import { CacheControl } from '../types/cache.type';

export const initialRequests = ['/cache/cf'];

@Injectable({
  providedIn: 'root',
})
export class CacheControlInterceptor implements HttpInterceptor {

  private _cache = inject(CFCacheService);

  /**
   * The function intercepts HTTP requests in a TypeScript application, checks if the request is to a
   * specific CMS and if certain initial requests are present, and then modifies the request by adding
   * a cache version parameter before passing it to the next handler.
   * @param req - HttpRequest<any> - Represents an HTTP request, which includes information such as the
   * URL, headers, and body of the request. It is used to make HTTP requests to a server.
   * @param {HttpHandler} next - `next` is an HttpHandler parameter in the `intercept` method.
   * @returns The `intercept` method returns an Observable of type `HttpEvent<any>`.
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this._isRequestToCms(req) || initialRequests.some(r => req.url.includes(r))) {
      return next.handle(req);
    }

    const version = this._getCacheVersion(this._cache.cacheControl(), req.url);
    if (version) {
      req = req.clone({
        setParams: { bcv: String(version) },
      });
    }
    return next.handle(req);
  }

  /**
   * Check request to CMS
   * @param req
   * @private
   */
  private _isRequestToCms(req: HttpRequest<any>): boolean {
    return (req.url.startsWith(environment.cms_host) || req.url.startsWith(environment.api_front_host)) && req.method === 'GET';
  }

  /**
   * Get cache version
   * @private
   */
  private _getCacheVersion(cache: CacheControl[] = [], requestUrl): string {
    const cacheItem = cache.find(c => requestUrl.includes(c.cacheKey));
    return cacheItem ? String(cacheItem.version) : null;
  }
}
